import { Link, useParams } from "react-router-dom";
import FanchaLayout from "@/components/feature/FanchaLayout";
import useSeo from "@/hooks/useSeo";
import { articles } from "@/mocks/fancha";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const item = articles.find((a) => a.id === id);
  const others = articles.filter((a) => a.id !== id).slice(0, 5);

  useSeo({
    title: item
      ? `${item.title} - 反差吃瓜局`
      : "文章未找到 | 反差吃瓜局",
    description: item
      ? item.summary
      : "没有找到对应的文章。",
    keywords: `反差吃瓜,反差吃瓜人设反转,${item ? item.category : ""},反差吃瓜前后对比`,
    canonicalPath: `/article/${id}`,
    jsonLd: item
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: item.title,
            description: item.summary,
            articleSection: item.category,
            datePublished: item.date,
            keywords: item.tags.join(","),
            author: { "@type": "Organization", name: "反差吃瓜局" },
            articleBody: item.paragraphs.join(" "),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "/" },
              { "@type": "ListItem", position: 2, name: "最新深扒观察", item: "/#latest" },
              { "@type": "ListItem", position: 3, name: item.title },
            ],
          },
        ]
      : undefined,
  });

  if (!item) {
    return (
      <FanchaLayout>
        <div className="mx-auto flex max-w-3xl flex-col items-center px-4 py-24 text-center">
          <i className="ri-file-warning-line text-5xl text-foreground-400" />
          <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950">没有找到这篇文章</h1>
          <p className="mt-2 text-sm text-foreground-600">它可能已被删除或地址有误，回到首页看看最新深扒吧。</p>
          <Link
            to="/#latest"
            className="mt-6 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            返回最新深扒
          </Link>
        </div>
      </FanchaLayout>
    );
  }

  return (
    <FanchaLayout>
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
          <Link to="/" className="whitespace-nowrap hover:text-primary-700">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/#latest" className="whitespace-nowrap hover:text-primary-700">最新深扒观察</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="whitespace-nowrap font-medium text-foreground-800">正文</span>
        </nav>

        <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-3">
          {/* 正文 */}
          <article className="lg:col-span-2">
            <header>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">{item.category}</span>
                <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-800">{item.source}</span>
              </div>
              <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
                {item.title}
              </h1>
              <p className="mt-4 font-heading text-lg leading-8 text-foreground-700">{item.summary}</p>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200/70 py-4 text-sm text-foreground-500">
                <span className="inline-flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-foreground-950 text-background-50">
                    <i className="ri-pencil-line" />
                  </span>
                  <span className="whitespace-nowrap font-medium text-foreground-800">反差吃瓜局 · 编辑部</span>
                </span>
                <time dateTime={item.date} className="whitespace-nowrap inline-flex items-center gap-1"><i className="ri-time-line" /> {item.date}</time>
                <span className="whitespace-nowrap inline-flex items-center gap-1"><i className="ri-timer-line" /> 阅读约 {item.minutes} 分钟</span>
                <span className="whitespace-nowrap inline-flex items-center gap-1"><i className="ri-eye-line" /> {item.views} 浏览</span>
              </div>
            </header>

            <div className="mt-6 aspect-[16/9] overflow-hidden rounded-3xl">
              <img src={item.cover} alt={item.title} className="h-full w-full object-cover" />
            </div>

            <div className="prose-fc mt-8">
              {item.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-2 border-t border-background-200/70 pt-6">
              {item.tags.map((t) => (
                <Link
                  key={t}
                  to={`/?q=${encodeURIComponent(t.replace("#", ""))}`}
                  className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-sm text-secondary-800 transition-colors hover:bg-secondary-200"
                >
                  {t}
                </Link>
              ))}
            </div>
          </article>

          {/* 侧栏 */}
          <aside className="space-y-6 lg:col-span-1">
            <div className="rounded-2xl border border-background-200/70 bg-background-100/60 p-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-accent-600">READ MORE</p>
              <h2 className="mt-1 font-heading text-lg font-bold text-foreground-950">继续阅读</h2>
              <ul className="mt-4 space-y-4">
                {others.map((a) => (
                  <li key={a.id}>
                    <Link to={`/article/${a.id}`} className="group block">
                      <h3 className="font-heading text-sm font-bold leading-snug text-foreground-900 group-hover:text-primary-700">
                        {a.title}
                      </h3>
                      <p className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
                        <span className="rounded bg-accent-100 px-1.5 text-accent-800">{a.category}</span>
                        <span>{a.date}</span>
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl bg-foreground-950 p-6 text-background-100">
              <p className="flex items-center gap-2 font-semibold text-accent-300">
                <i className="ri-flashlight-line" /> 有理有据地吃瓜
              </p>
              <p className="mt-2 text-sm leading-6 text-background-200/80">
                想第一时间看到最新反转深扒？订阅「反差吃瓜局」的方式很简单，留下线索或常回来看看。
              </p>
              <Link
                to="/#contribute"
                className="mt-4 inline-flex w-full whitespace-nowrap items-center justify-center rounded-full bg-accent-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
              >
                提供线索
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </FanchaLayout>
  );
}